import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Users, Plus, Search, Edit, Trash2, X, Save, UserCheck, Building2, Phone, Mail, MapPin, Calendar } from 'lucide-react';

const DEPARTMENTS = ['Engineering', 'Marketing', 'Sales', 'HR', 'Finance', 'Operations', 'Product', 'Design', 'Legal', 'Customer Support', 'Other'];
const STATUSES = ['active', 'on_leave', 'terminated', 'contractor'];
const TYPES = ['full-time', 'part-time', 'contractor', 'intern'];

export default function EmployeeDirectory() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', department: '', title: '', manager: '', location: '', start_date: '', status: 'active', employee_type: 'full-time', emergency_contact: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const loadData = React.useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (deptFilter) params.set('department', deptFilter);
      if (statusFilter) params.set('status', statusFilter);
      const r = await api.get(`/api/employees?${params}`);
      setEmployees(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, deptFilter, statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => { const t = setTimeout(loadData, 300); return () => clearTimeout(t); }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingEmp) {
        await api.patch(`/api/employees/${editingEmp.id}`, form);
      } else {
        await api.post('/api/employees', form);
      }
      setShowAdd(false); setEditingEmp(null);
      setForm({ first_name: '', last_name: '', email: '', phone: '', department: '', title: '', manager: '', location: '', start_date: '', status: 'active', employee_type: 'full-time', emergency_contact: '', notes: '' });
      loadData();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleEdit = (emp) => {
    setEditingEmp(emp);
    setForm({ first_name: emp.first_name || '', last_name: emp.last_name || '', email: emp.email || '', phone: emp.phone || '', department: emp.department || '', title: emp.title || '', manager: emp.manager || '', location: emp.location || '', start_date: emp.start_date || '', status: emp.status || 'active', employee_type: emp.employee_type || 'full-time', emergency_contact: emp.emergency_contact || '', notes: emp.notes || '' });
    setShowAdd(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this employee?')) return;
    try { await api.delete(`/api/employees/${id}`); loadData(); } catch (e) { console.error(e); }
  };

  const statusColor = { active: '#dcfce7', on_leave: '#fef3c7', terminated: '#fef2f2', contractor: '#dbeafe' };
  const statusText = { active: '#166534', on_leave: '#92400e', terminated: '#991b1b', contractor: '#1e40af' };

  const activeCount = employees.filter(e => e.status === 'active').length;
  const depts = [...new Set(employees.map(e => e.department).filter(Boolean))];

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="fade-in" style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Employee Directory</h3>
          <p style={{ fontSize: 14, color: '#64748b' }}>{employees.length} employees ({activeCount} active)</p>
        </div>
        <button className="btn-action" onClick={() => { setEditingEmp(null); setForm({ first_name: '', last_name: '', email: '', phone: '', department: '', title: '', manager: '', location: '', start_date: '', status: 'active', employee_type: 'full-time', emergency_contact: '', notes: '' }); setShowAdd(true); }} data-testid="add-employee">
          <Plus size={16} style={{ marginRight: 6 }} />Add Employee
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 12, color: '#94a3b8' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or title..."
            style={{ width: '100%', padding: '10px 12px 10px 36px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none' }} data-testid="search-employees" />
        </div>
        <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} style={{ padding: '10px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13, minWidth: 150 }} data-testid="filter-dept">
          <option value="">All Departments</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: '10px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13, minWidth: 120 }} data-testid="filter-status">
          <option value="">All Status</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Employee Table */}
      {employees.length === 0 ? (
        <div className="empty-state"><Users size={64} /><h4>No Employees</h4><p>Add employees to build your company directory.</p></div>
      ) : (
        <table className="audit-log-table" data-testid="employee-table">
          <thead><tr><th>Name</th><th>Title</th><th>Department</th><th>Email</th><th>Status</th><th>Start Date</th><th>Actions</th></tr></thead>
          <tbody>
            {employees.map(emp => (
              <tr key={emp.id}>
                <td style={{ fontWeight: 600 }}>{emp.first_name} {emp.last_name}</td>
                <td>{emp.title}</td>
                <td><span style={{ padding: '2px 8px', background: '#f1f5f9', borderRadius: 6, fontSize: 12 }}>{emp.department}</span></td>
                <td style={{ fontSize: 12, color: '#64748b' }}>{emp.email}</td>
                <td><span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: statusColor[emp.status] || '#f1f5f9', color: statusText[emp.status] || '#475569' }}>{emp.status}</span></td>
                <td style={{ fontSize: 12 }}>{emp.start_date}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => handleEdit(emp)} className="btn-secondary" style={{ padding: '4px 8px', fontSize: 10 }}><Edit size={12} /></button>
                    <button onClick={() => handleDelete(emp.id)} className="btn-delete" style={{ padding: '4px 8px', fontSize: 10 }}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add/Edit Modal */}
      {showAdd && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setShowAdd(false)}>
          <div className="task-modal slide-up" style={{ maxWidth: 600, maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>{editingEmp ? 'Edit Employee' : 'Add Employee'}</h3>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group"><label>First Name</label><input value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} placeholder="John" data-testid="emp-first-name" /></div>
              <div className="form-group"><label>Last Name</label><input value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} placeholder="Doe" /></div>
              <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="john@company.com" /></div>
              <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="(555) 123-4567" /></div>
              <div className="form-group"><label>Title</label><input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Software Engineer" /></div>
              <div className="form-group"><label>Department</label>
                <select value={form.department} onChange={e => setForm(f => ({...f, department: e.target.value}))} style={{ width: '100%', padding: '10px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                  <option value="">Select...</option>{DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Manager</label><input value={form.manager} onChange={e => setForm(f => ({...f, manager: e.target.value}))} placeholder="Manager name" /></div>
              <div className="form-group"><label>Location</label><input value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} placeholder="New York, NY" /></div>
              <div className="form-group"><label>Start Date</label><input type="date" value={form.start_date} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} /></div>
              <div className="form-group"><label>Status</label>
                <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} style={{ width: '100%', padding: '10px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Type</label>
                <select value={form.employee_type} onChange={e => setForm(f => ({...f, employee_type: e.target.value}))} style={{ width: '100%', padding: '10px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                  {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Emergency Contact</label><input value={form.emergency_contact} onChange={e => setForm(f => ({...f, emergency_contact: e.target.value}))} placeholder="Name - Phone" /></div>
            </div>
            <div className="form-group" style={{ marginTop: 8 }}><label>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Additional notes..." style={{ width: '100%', minHeight: 60, padding: 10, border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
            <button className="btn-action" onClick={handleSave} disabled={saving || !form.first_name || !form.last_name} style={{ width: '100%', marginTop: 8 }} data-testid="save-employee">
              <Save size={14} style={{ marginRight: 6 }} />{saving ? 'Saving...' : editingEmp ? 'Update Employee' : 'Add Employee'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
